// Plan 06-05 — LoginPage admin: login (senha+TOTP) + deriva/grava KEK_admin_ti (ADM-01).
//
// 🔴 PASSO 2 — LIVENESS (24/08). O admin passou a ter reconhecimento facial no login, como o
// comitê. Motivo: `admin_ti` cria conta, cadastra rosto alheio e destrava lockout biométrico;
// é o papel que pode FABRICAR acesso, e estava protegido por dois fatores enquanto quem ele
// cria era protegido por três.
//
// O componente é o MESMO do comitê (`@aions/ui-primitives`), com as chamadas injetadas —
// extraído em 24/08 justamente para não existirem duas máquinas de liveness divergindo.
import { useEffect, useState, type FormEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { Alert, Button, Field, Input, LivenessCapture } from '@aions/ui-primitives';
import { loginWithPassword, drawLivenessChallenge, verifyLiveness } from '../lib/auth-client';
import { deriveKekAdminTi, storeKekAdminTi } from '../lib/kek-admin-session';
import { useSession } from '../contexts/SessionContext';

export function LoginPage({ onSuccess }: { onSuccess: () => void }): JSX.Element {
  const { t } = useTranslation();
  const { setSession } = useSession();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  // DEV-ONLY — pré-preenche o TOTP no `vite dev` (o backend dispensa o código com
  // TOTP_DEV_BYPASS). MODE==='development' é o `vite dev`; produção ('production')
  // e testes ('test', vitest) ficam com o campo vazio.
  const [totp, setTotp] = useState(import.meta.env.MODE === 'development' ? '000000' : '');
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [step, setStep] = useState<'credentials' | 'liveness'>('credentials');
  // 🔴 SESSÃO SEGURADA ATÉ A BIOMETRIA PASSAR (24/08).
  //
  // O `App` do admin decide a tela por `if (!session)` (App.tsx:213) — não por rota, como o
  // comitê. Publicar a sessão no contexto logo após a senha fazia o App trocar para o
  // dashboard NO MESMO RENDER, e o passo da câmera nunca chegava a montar: o login "passava"
  // sem reconhecimento facial nenhum. Foi assim que o teste de 24/08 pegou.
  //
  // O token JÁ está no sessionStorage (o `loginWithPassword` do auth-client grava), então o
  // `apiFetch` do liveness sai autenticado — o que se segura aqui é só a publicação no
  // CONTEXTO, que é o que controla a navegação.
  const [sessaoPendente, setSessaoPendente] =
    useState<Awaited<ReturnType<typeof loginWithPassword>>['session']>(undefined);

  // Mesmo escape hatch do comitê, e pela mesma razão: em `vite dev` o padrão é EXIGIR a
  // câmera; pular é ato explícito (`VITE_DEV_SKIP_LIVENESS=true`). Produção ('production') e
  // testes ('test') não olham para esta variável.
  useEffect(() => {
    if (step !== 'liveness') return;
    const pularEmDev =
      import.meta.env.MODE === 'development' && import.meta.env.VITE_DEV_SKIP_LIVENESS === 'true';
    if (pularEmDev) {
      if (sessaoPendente) setSession(sessaoPendente);
      onSuccess();
    }
  }, [step, onSuccess, sessaoPendente, setSession]);

  const handleSubmit = async (e: FormEvent): Promise<void> => {
    e.preventDefault();
    setError(null);
    setLoading(true);
    const result = await loginWithPassword(email, password, totp);
    setLoading(false);
    if (!result.ok || !result.session) {
      setError(result.error ?? 'invalid_credentials');
      return;
    }
    // NÃO publica no contexto ainda — ver `sessaoPendente` acima.
    setSessaoPendente(result.session);
    // ADM-01 — deriva KEK_admin_ti (Argon2id: senha + kekSalt) e grava cifrada na sessão,
    // espelhando web-comite/LoginPage. Necessária p/ wrap do PDF de nomeação ao criar membro
    // (PdfUploadWidget em MembersPage). Cifrada sob o segredo de sessão não-extraível
    // (fix 02.8-05 — não mais o token; ver kek-admin-session.ts). Falha aqui NÃO bloqueia
    // o login — só deixaria a criação de membro indisponível (MembersPage surfaça error_kek_missing).
    const salt = result.session.user.kekSalt;
    if (salt) {
      try {
        const kekRaw = await deriveKekAdminTi(password, salt);
        try {
          await storeKekAdminTi(kekRaw);
        } finally {
          kekRaw.fill(0); // zera o material cru após gravar (paridade D-191)
        }
      } catch {
        // Derivação/store falhou — login segue válido; criar membro ficará indisponível.
      }
    }
    // A KEK deriva ANTES da câmera de propósito (Argon2id é o passo caro e não depende de
    // biometria) — paridade com o comitê, onde os dois correm em paralelo no passo 2.
    setStep('liveness');
  };

  if (step === 'liveness') {
    return (
      <div style={{ maxWidth: 440, margin: '40px auto' }}>
        <LivenessCapture
          onVerified={() => {
            // Só agora a sessão vai ao contexto: é este ato que libera a navegação.
            if (sessaoPendente) setSession(sessaoPendente);
            onSuccess();
          }}
          drawChallenge={drawLivenessChallenge}
          verify={verifyLiveness}
        />
      </div>
    );
  }

  return (
    <div style={{ maxWidth: 440, margin: '40px auto' }}>
      <div className="card">
        <h1>{t('login.title')}</h1>

        {error && (
          <Alert variant="error" style={{ marginBottom: 16 }}>
            {t(`login.errors.${error}`, { defaultValue: t('login.errors.invalid_credentials') })}
          </Alert>
        )}

        <form onSubmit={(e) => void handleSubmit(e)} aria-label={t('login.title')}>
          <Field label={t('login.email_label')}>
            {({ id }) => (
              <Input
                id={id}
                name="email"
                type="email"
                autoComplete="username"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
              />
            )}
          </Field>

          <Field label={t('login.password_label')}>
            {({ id }) => (
              <Input
                id={id}
                name="password"
                type="password"
                autoComplete="current-password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                minLength={12}
              />
            )}
          </Field>

          <Field label={t('login.totp_label')} help={t('login.totp_help')}>
            {({ id, describedBy }) => (
              <Input
                id={id}
                aria-describedby={describedBy}
                name="totp"
                type="text"
                inputMode="numeric"
                autoComplete="one-time-code"
                mono
                value={totp}
                onChange={(e) => setTotp(e.target.value.replace(/\D/g, '').slice(0, 6))}
                required
                pattern="[0-9]{6}"
                maxLength={6}
                style={{ letterSpacing: '0.4em' }}
              />
            )}
          </Field>

          <Button type="submit" block loading={loading} disabled={loading}>
            {t('login.submit')}
          </Button>
        </form>
      </div>
    </div>
  );
}
